import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import Tooltip from '../../../../../shared/components/widgets/Tooltip';
import style, { DROPDOWN_COLORS } from '../../../../../shared/components/themeSupport';
import './GoogleConstantMultiSelect.scss';

/**
 * MpbtStaticMultiSelect
 *
 * A working multi-select for MPBT fields backed by a fixed, static list of options
 * (e.g. Location Types, normalized Languages) - as opposed to GoogleConstantMultiSelect,
 * which searches a live API. Exists because the app-wide shared Select field renderer
 * (src/shared/components/form/Select.jsx) never forwards `isMulti` down to react-select,
 * so passing isMulti to it silently produces a single-select that returns a single
 * option object instead of an array, breaking any code that expects an array.
 *
 * The redux-form field value is stored as a plain array of raw option values (e.g.
 * ['home', 'recent']), not an array of {label, value} objects, so downstream
 * serialization code doesn't need to know about react-select's option shape.
 */
const MpbtStaticMultiSelect = ({
  input, meta, label, tooltip, placeholder, options,
}) => {
  const theme = useSelector(store => store.theme);
  const themeColors = style(theme, DROPDOWN_COLORS);
  const textColor = theme.className.endsWith('light') ? '#000000' : '#FFFFFF';

  const selectedValues = Array.isArray(input.value) ? input.value : [];
  const selectedOptions = options.filter(option => selectedValues.includes(option.value));

  const handleChange = (newSelectedOptions) => {
    input.onChange((newSelectedOptions || []).map(option => option.value));
  };

  return (
    <div className="form__form-group">
      <span className="form__form-group-label">
        {label}{' '}
        {tooltip && <Tooltip id={`${input.name}-tooltip`} msg={tooltip} placement="top" />}
      </span>
      <div className="form__form-group-field">
        <div className="google-constant-multi-select" style={{ width: '100%' }}>
          <Select
            isMulti
            value={selectedOptions}
            options={options}
            onChange={handleChange}
            placeholder={placeholder}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            theme={selectTheme => ({
              ...selectTheme,
              colors: {
                ...selectTheme.colors,
                primary: `${themeColors.colorPrimary}`,
                neutral0: `${themeColors.colorBackground}`,
                neutral80: textColor,
              },
            })}
            classNamePrefix="react-select"
          />
        </div>
        {meta?.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
      </div>
    </div>
  );
};

MpbtStaticMultiSelect.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })),
};

MpbtStaticMultiSelect.defaultProps = {
  meta: null,
  tooltip: null,
  placeholder: 'Select...',
  options: [],
};

export default MpbtStaticMultiSelect;
